import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrdenesPermisos } from '../../hooks/useOrdenesPermisos';
import { ordenesNotificacionesAPI } from '../../lib/ordenesApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
    Alert,
    AlertDescription,
} from '../../components/ui/alert';
import { toast } from 'sonner';
import {
    Bell,
    ArrowLeft,
    Mail,
    CheckCircle,
    Loader2,
    Info,
} from 'lucide-react';

const EVENTOS = [
    {
        id: 'orden_completada',
        nombre: 'Orden Completada',
        descripcion: 'Enviar email cuando se completa una orden de servicio',
        icon: '✅'
    },
    {
        id: 'orden_facturada',
        nombre: 'Orden Facturada',
        descripcion: 'Enviar email cuando se emite factura para una orden',
        icon: '💰'
    },
    {
        id: 'orden_asignada',
        nombre: 'Orden Asignada',
        descripcion: 'Enviar email cuando se asigna un técnico a una orden',
        icon: '🔧'
    },
];

export default function OrdenesNotificacionesPage() {
    const navigate = useNavigate();
    const { tienePermiso, esAdmin, loading: permisosLoading } = useOrdenesPermisos();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState({});

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = async () => {
        try {
            setLoading(true);
            const res = await ordenesNotificacionesAPI.getConfig();
            const configMap = {};
            res.data.forEach(c => {
                configMap[c.evento] = c;
            });
            setConfig(configMap);
        } catch (err) {
            console.error('Error loading config:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async (evento, activa) => {
        const currentConfig = config[evento] || { evento, activa: false, email_destino: null };
        
        setSaving(true);
        try {
            await ordenesNotificacionesAPI.updateConfig({
                evento,
                activa,
                email_destino: currentConfig.email_destino
            });
            
            setConfig(prev => ({
                ...prev,
                [evento]: { ...currentConfig, activa }
            }));
            
            toast.success(activa ? 'Notificación activada' : 'Notificación desactivada');
        } catch (err) {
            toast.error('Error al actualizar configuración');
        } finally {
            setSaving(false);
        }
    };

    const handleEmailChange = (evento, email) => {
        setConfig(prev => ({
            ...prev,
            [evento]: { ...(prev[evento] || {}), evento, email_destino: email }
        }));
    };

    const handleSaveEmail = async (evento) => {
        const currentConfig = config[evento] || {};
        
        setSaving(true);
        try {
            await ordenesNotificacionesAPI.updateConfig({
                evento,
                activa: currentConfig.activa || false,
                email_destino: currentConfig.email_destino
            });
            toast.success('Email guardado');
        } catch (err) {
            toast.error('Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    if (permisosLoading || loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    if (!esAdmin && !tienePermiso('configurar_notificaciones')) {
        return (
            <div className="p-6 text-center">
                <Bell className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h2 className="text-xl font-bold mb-2">Acceso Restringido</h2>
                <p className="text-muted-foreground">No tienes permisos para configurar notificaciones.</p>
                <Button variant="outline" className="mt-4" onClick={() => navigate('/ordenes/configuracion')}>
                    Volver
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate('/ordenes/configuracion')} data-testid="back-btn">
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Bell className="h-6 w-6" />
                        Notificaciones por Email
                    </h1>
                    <p className="text-muted-foreground">Configura alertas automáticas para eventos de órdenes</p>
                </div>
            </div>

            <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                    Las notificaciones se envían automáticamente al cliente cuando ocurre el evento. 
                    El email se obtiene de la ficha del cliente asociado a la orden.
                </AlertDescription>
            </Alert>

            {/* Configuración por evento */}
            <div className="grid gap-4">
                {EVENTOS.map(evento => {
                    const eventoConfig = config[evento.id] || { activa: false, email_destino: null };
                    
                    return (
                        <Card key={evento.id} className={`border-2 ${eventoConfig.activa ? 'border-green-200' : ''}`}>
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <span className="text-2xl">{evento.icon}</span>
                                        <div>
                                            <CardTitle className="text-base">{evento.nombre}</CardTitle>
                                            <CardDescription>{evento.descripcion}</CardDescription>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={eventoConfig.activa}
                                        onCheckedChange={(checked) => handleToggle(evento.id, checked)}
                                        disabled={saving}
                                    />
                                </div>
                            </CardHeader>
                            {eventoConfig.activa && (
                                <CardContent className="pt-2">
                                    <div className="flex items-end gap-2">
                                        <div className="flex-1">
                                            <Label className="text-sm text-muted-foreground">
                                                Email adicional (copia, opcional)
                                            </Label>
                                            <Input
                                                type="email"
                                                placeholder="admin@empresa.com"
                                                value={eventoConfig.email_destino || ''}
                                                onChange={(e) => handleEmailChange(evento.id, e.target.value)}
                                            />
                                        </div>
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            onClick={() => handleSaveEmail(evento.id)}
                                            disabled={saving}
                                        >
                                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}
                                        </Button>
                                    </div>
                                </CardContent>
                            )}
                        </Card>
                    );
                })}
            </div>

            {/* Estado del servicio */}
            <Card className="border-2">
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Estado del Servicio de Email
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle className="h-4 w-4" />
                        <span>Servicio Brevo configurado correctamente</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                        Remitente: contacto@4satgps.cl
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}
